import { useEffect, useState } from "react";
import { EditIcon, ExternalLinkIcon, TrashIcon } from "lucide-react";
import { useNavigate } from "react-router";
import { DataTable, type Column } from "@/components/custom/data-table";
import { ConfirmationDialog } from "@/components/custom/confirmation-dialog";
import { Form } from "@/pages/dashboard/tenant/extensions/form";
import { extensionsApi } from "@/lib/api/extensions";
import { hasMappedExtension } from "@/extensions/registry";
import type {
  Extension,
  ExtensionCreateInput,
  ExtensionUpdateInput,
} from "@/types/entities";
import { Button } from "@/components/ui/button";

interface ExtensionsTableProps {
  tenantSlug: string;
  showHeader?: boolean;
  headerTitle?: string;
  headerDescription?: string;
}

export function ExtensionsTable({
  tenantSlug,
  showHeader = true,
  headerTitle = "Extensions",
  headerDescription = "Manage tenant extension configurations",
}: ExtensionsTableProps) {
  const navigate = useNavigate();
  const [extensions, setExtensions] = useState<Extension[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingExtension, setEditingExtension] = useState<Extension | undefined>();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [extensionToDelete, setExtensionToDelete] = useState<Extension | undefined>();
  const [error, setError] = useState<string | null>(null);

  const fetchExtensions = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await extensionsApi.getAllForTenant(tenantSlug);
      setExtensions(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load extensions");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchExtensions();
  }, [tenantSlug]);

  const handleCreate = () => {
    setEditingExtension(undefined);
    setIsDialogOpen(true);
  };

  const handleEdit = (extension: Extension) => {
    setEditingExtension(extension);
    setIsDialogOpen(true);
  };

  const handleDelete = (extension: Extension) => {
    setExtensionToDelete(extension);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!extensionToDelete) return;

    try {
      await extensionsApi.delete(tenantSlug, extensionToDelete.id);
      setDeleteDialogOpen(false);
      setExtensionToDelete(undefined);
      fetchExtensions();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to delete extension");
    }
  };

  const handleSubmit = async (data: ExtensionCreateInput | ExtensionUpdateInput) => {
    if (editingExtension) {
      await extensionsApi.update(tenantSlug, editingExtension.id, data as ExtensionUpdateInput);
    } else {
      await extensionsApi.create(tenantSlug, data as ExtensionCreateInput);
    }

    fetchExtensions();
    setIsDialogOpen(false);
    setEditingExtension(undefined);
  };

  const columns: Column<Extension>[] = [
    {
      key: "id",
      header: "ID",
      cell: (extension) => <span className="text-muted-foreground">#{extension.id}</span>,
      className: "w-20",
    },
    {
      key: "name",
      header: "Name",
      cell: (extension) => (
        <div>
          <div className="font-mono text-sm">{extension.name}</div>
          {extension.display_name ? (
            <div className="text-xs text-muted-foreground">{extension.display_name}</div>
          ) : null}
        </div>
      ),
    },
    {
      key: "type",
      header: "Type",
      cell: (extension) => (
        <span className="inline-flex items-center rounded-md bg-muted px-2 py-1 text-xs font-medium text-muted-foreground">
          {extension.type}
        </span>
      ),
      className: "w-28",
    },
    {
      key: "endpoint",
      header: "Endpoint",
      cell: (extension) => (
        <div className="font-mono text-sm">
          <div className="text-xs text-muted-foreground">{extension.endpoint_method}</div>
          <div>{extension.endpoint_path}</div>
        </div>
      ),
    },
    {
      key: "enabled",
      header: "Enabled",
      cell: (extension) => (
        <span className="inline-flex items-center rounded-md bg-muted px-2 py-1 text-xs font-medium text-muted-foreground">
          {extension.enabled ? "Yes" : "No"}
        </span>
      ),
      className: "w-24",
    },
    {
      key: "actions",
      header: "Actions",
      cell: (extension) => (
        <div className="flex items-center gap-2">
          {extension.enabled && extension.type === "cms" && hasMappedExtension(tenantSlug, extension.name) ? (
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate(`/admin/dashboard/tenants/${tenantSlug}/extensions/${extension.name}`)}
              className="h-8 w-8"
              title="Open extension"
            >
              <ExternalLinkIcon className="size-4" />
            </Button>
          ) : null}
          <Button
            variant="ghost"
            size="icon"
            onClick={() => handleEdit(extension)}
            className="h-8 w-8"
          >
            <EditIcon className="size-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => handleDelete(extension)}
            className="h-8 w-8 text-destructive hover:text-destructive"
          >
            <TrashIcon className="size-4" />
          </Button>
        </div>
      ),
      className: "w-24",
    },
  ];

  return (
    <div className="space-y-4">
      {showHeader && (
        <div>
          <h1 className="text-2xl font-bold">{headerTitle}</h1>
          <p className="text-muted-foreground">{headerDescription}</p>
        </div>
      )}

      {error && <div className="bg-destructive/10 text-destructive p-3 rounded-md">{error}</div>}

      <DataTable
        data={extensions}
        columns={columns}
        searchKeys={["name", "display_name", "type", "endpoint_path", "endpoint_method"]}
        searchPlaceholder="Search extensions..."
        emptyMessage="No extensions found"
        isLoading={isLoading}
        onCreate={handleCreate}
        createLabel="Add Extension"
      />

      <Form
        open={isDialogOpen}
        onOpenChange={setIsDialogOpen}
        onSubmit={handleSubmit}
        onCancel={() => {
          setIsDialogOpen(false);
          setEditingExtension(undefined);
        }}
        extension={editingExtension}
      />

      <ConfirmationDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        title="Delete Extension"
        description={`Are you sure you want to delete "${extensionToDelete?.name}"? This action cannot be undone.`}
        onConfirm={confirmDelete}
        confirmVariant="destructive"
        confirmLabel="Delete"
      />
    </div>
  );
}
